import { ActionButton, ActionIconButton, TableColumnHeader, TableColumnHeaderWithHoverCard } from "@/components/shared/table/Table";
import { Document, DocumentFormat, DocumentStatus, Service, ServicePermission, ServiceType, Shift, TenderDurationFragment } from "@/graphql/__generated__/graphql";
import { formatDateLocale, formatNumberLocale } from "@/utils/helpers";
import { ColumnDef, Row } from "@tanstack/react-table";
import { TFunction } from "i18next";
import { IntlShape } from "react-intl";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { DotsHorizontalIcon } from "@radix-ui/react-icons";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { LuBookText } from "react-icons/lu";
import { Badge } from "@/components/ui/badge";
import { BadgeColourMap } from "@/constants/colours";
import { Button } from "@/components/ui/button";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { cn } from "@/lib/utils";
import { CalculatorIcon, EyeIcon, FileSpreadsheet, InfoIcon, Loader2Icon, PencilIcon, TrashIcon } from "lucide-react";

export const getColumnsServices = (intl: IntlShape, t: TFunction, removeActionFn: any, editActionFn: any, detailsActionFn: any): ColumnDef<Service, string>[] => {

  /**
   * Determines if a row can be edited based on the 'isEditable' and 'permissions' fields.
   *
   * @param {Row<Service>} row - The row of type `Row<Service>` to check for edit permissions.
   * @returns {boolean} `true` if the row is editable and the user has edit permissions, otherwise `false`.
   */
  const canEdit = (row: Row<Service>): boolean => {
    return row.original.permissions.includes(ServicePermission.Edit);
  };

  return [
    {
      accessorKey: "name",
      header: ({ column }) => <TableColumnHeader hideSortingButton text={t("tables:services.columns.name")} column={column} />,
      cell: ({ row }) => row.original.name,
    },
    {
      accessorKey: "site.name",
      header: ({ column }) => <TableColumnHeader hideSortingButton text={t("tables:services.columns.site")} column={column} />,
      cell: ({ row }) => row.original.site.name,
    },
    {
      accessorKey: "margin",
      header: ({ column }) => <TableColumnHeader hideSortingButton text={t("tables:services.columns.margin")} column={column} align="end" />,
      cell: ({ row }) => <div className="text-right">{`${row.original.margin / 100}%`}</div>,
    },
    {
      accessorKey: "revenue",
      header: ({ column }) =>
        <TableColumnHeaderWithHoverCard
          hideSortingButton
          text={t("tables:services.columns.totalpriceperhour")}
          column={column}
          hoverCardText={t("tables:services.tooltips.totalpriceperhour")}
          icon={<InfoIcon className="h-4 w-4 text-muted-foreground" />}
          align="end"
        />,
      cell: ({ row }) => {
        if (!row) return 0;
        const totalPricePerHour = row.original.pricingTotalPricePerHour?.totalPricePerHour ?? 0;
        return <div className="text-right">{formatNumberLocale(intl, totalPricePerHour, "decimal") + " €/h"}</div>;
      },
    },
    {
      accessorKey: "hourlyWage",
      header: ({ column }) => <TableColumnHeader hideSortingButton text={t("tables:services.columns.wage")} column={column} align="end" />,
      cell: ({ row }) =>
        <div className="text-right">{formatNumberLocale(intl, row.original.hourlyWage, "currency") + `${row.original.nonTariffAllowance > 0 ? ` + ${formatNumberLocale(intl, row.original.nonTariffAllowance, "currency")}` : ""}`}</div>,
    },
    {
      accessorKey: "minutesTotal",
      header: ({ column }) => <TableColumnHeader hideSortingButton text={t("tables:services.columns.hours")} column={column} align="end" />,
      cell: ({ row }) => <div className="text-right">{formatNumberLocale(intl, row.original.minutesTotal / 60, "decimal")}</div>,
    },
    {
      accessorKey: "type",
      header: ({ column }) => <TableColumnHeader hideSortingButton text={t("tables:services.columns.type")} column={column} />,
      cell: ({ row }) => t("common:servicetype." + row.original.type.toLowerCase()),
    },
    {
      accessorKey: "pauseIncluded",
      header: ({ column }) => <TableColumnHeader hideSortingButton text={t("tables:services.columns.pause")} column={column} />,
      cell: ({ row }) => row.original.type === ServiceType.Mobile ? t("tables:services.notavailable") : row.original.pauseIncluded ? t("common:yes") : t("common:no"),
    },
    {
      id: "actions",
      size: 140,
      minSize: 140,
      maxSize: 140,
      cell: ({ row }) => {
        return (
          <div className="flex gap-2 justify-end">
            <ActionIconButton
              dataObj={row.original}
              fn={detailsActionFn}
              tooltipText={t("tables:services.pricingbreakdown")}
            >
              <CalculatorIcon className="h-4 w-4" />
            </ActionIconButton>

            <ActionIconButton
              dataObj={row.original}
              fn={editActionFn}
              tooltipText={canEdit(row) ? t("common:buttons.edit") : t("common:buttons.view")}
            >
              {canEdit(row) ? (
                <PencilIcon className="h-4 w-4" />
              ) : (
                <EyeIcon className="h-4 w-4" />
              )}
            </ActionIconButton>

            {canEdit(row) && (
              <ActionIconButton
                dataObj={row.original}
                fn={removeActionFn}
                tooltipText={t("common:buttons.remove")}
                variant="destructive"
              >
                <TrashIcon className="h-4 w-4" />
              </ActionIconButton>
            )}
          </div>
        );
      }
    }
  ].filter(Boolean) as ColumnDef<Service, string>[];
};

const DaysTextContainer = ({ t, row }: { t: TFunction; row: Row<Shift>; }) => {
  const selectedDays = Object.entries(
    {
      monday: row.original.monday,
      tuesday: row.original.tuesday,
      wednesday: row.original.wednesday,
      thursday: row.original.thursday,
      friday: row.original.friday,
      saturday: row.original.saturday,
      sunday: row.original.sunday,
      holiday: row.original.holiday,
    }
  ).filter(([, isActive]) => isActive)
    .map(([day]) => t("common:weekdaysshort." + day));

  return <div className="flex gap-2">{selectedDays.join(", ")}</div>;
};

const formatShiftTime = (type: "SHIFT" | "PAUSE", row): string => {
  switch (type) {
  case "SHIFT":
    return `${row.original.startTime}` + " - " + `${row.original.endTime}`;
  case "PAUSE":
    if (!row.original.pauseStartTime && !row.original.pauseEndTime) return "";
    return `${row.original.pauseStartTime}` + " - " + `${row.original.pauseEndTime}`;
  }
};

export const getColumnsShifts = (intl: IntlShape, t: TFunction, userCanEdit: boolean, removeActionFn: any, editActionFn: any, tenderDuration: TenderDurationFragment, isProcessing?: boolean): ColumnDef<Shift, string>[] => {

  return [
    {
      id: "shiftDuration",
      header: ({ column }) => <TableColumnHeader hideSortingButton text={t("tables:shifts.columns.shiftduration")} column={column} />,
      cell: ({ row }) => formatShiftTime("SHIFT", row),
    },
    {
      id: "pauseDuration",
      header: ({ column }) => <TableColumnHeader hideSortingButton text={t("tables:shifts.columns.pauseduration")} column={column} />,
      cell: ({ row }) => formatShiftTime("PAUSE", row),
    },
    {
      accessorKey: "employeeQuantity",
      header: ({ column }) => <TableColumnHeader hideSortingButton text={t("tables:shifts.columns.employees")} column={column} align="end" />,
      cell: ({ row }) => <div className="text-right">{row.original.employeeQuantity}</div>,
    },
    {
      // accessorKey: "",
      id: "days",
      header: ({ column }) => <TableColumnHeader hideSortingButton text={t("tables:shifts.columns.days")} column={column} />,
      cell: ({ row }) => (
        <DaysTextContainer t={t} row={row} />
      ),
    },
    {
      accessorKey: "applyHolidayPay",
      header: ({ column }) => <TableColumnHeader hideSortingButton text={t("tables:shifts.columns.holidaypay")} column={column} />,
      cell: ({ row }) => row.original.applyHolidayPay ? t("common:yes") : t("common:no"),
    },
    {
      id: "dateRange",
      header: ({ column }) => <TableColumnHeader hideSortingButton text={t("tables:shifts.columns.daterange")} column={column} />,
      cell: ({ row }) => {
        if (!row.original.startDate && !row.original.endDate) return t("common:entirecontractduration");

        const startDate = row.original.startDate ? formatDateLocale(intl, row.original.startDate) : formatDateLocale(intl, tenderDuration.contract.startDate);
        const endDate = row.original.endDate ? formatDateLocale(intl, row.original.endDate) : formatDateLocale(intl, tenderDuration.contract.endDate);
        return `${startDate} - ${endDate}`;
      },
    },
    userCanEdit ? {
      id: "actions",
      cell: ({ row }) => {
        return (
          <div className="flex gap-2 justify-end">
            <ActionIconButton
              dataObj={row.original}
              fn={editActionFn}
              tooltipText={t("common:buttons.edit")}
              disabled={isProcessing}
            >
              <PencilIcon className="h-4 w-4" />
            </ActionIconButton>

            <ActionIconButton
              dataObj={row.original}
              fn={removeActionFn}
              tooltipText={t("common:buttons.remove")}
              variant="destructive"
              disabled={isProcessing}
            >
              <TrashIcon className="h-4 w-4" />
            </ActionIconButton>
          </div>
        );
      },
    } : null
  ].filter(Boolean) as ColumnDef<Shift, string>[];
};

export const getColumnsDocuments = (
  intl: IntlShape,
  t: TFunction,
  userCanEdit: boolean,
  removeActionFn: any,
  addToKnowledgeBaseActionFn: any,
  removeFromKnowledgeBaseActionFn: any,
  downloadActionFn: any,
  loading: boolean
): ColumnDef<Document, string>[] => {
  return [
    {
      accessorKey: "fileName",
      header: ({ column }) => <TableColumnHeader hideSortingButton text={t("tables:documents.columns.filename")} column={column} />,
      cell: ({ row }) => <div className="break-all">{row.original.fileName}</div>,
    },
    {
      accessorKey: "type",
      header: ({ column }) => <TableColumnHeader hideSortingButton text={t("tables:documents.columns.type")} column={column} />,
      cell: ({ row }) => {
        const type = row.original.type;
        const badgeClass = BadgeColourMap[type] || "bg-gray-200";

        return (
          <Badge className={cn("text-xs bg-red-200 text-slate-900", badgeClass)}>{t("common:documenttype." + row.original.type.toLowerCase())}</Badge>
        );
      }
    },
    {
      accessorKey: "inKnowledgeBase",
      header: ({ column }) => {
        return (
          <div className="flex items-center gap-1">
            <TableColumnHeader hideSortingButton text="" icon={<LuBookText />} column={column} />
            <HoverCard>
              <HoverCardTrigger>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-5 w-5 p-0 hover:cursor-help flex items-center justify-center"
                >
                  <InformationCircleIcon className="h-5 w-5" />
                </Button>
              </HoverCardTrigger>
              <HoverCardContent >
                <h2 className="text-sm mb-2">{t("edittender.documents.knowledgebase")}</h2>
                <p className="text-sm font-normal text-slate-400">{t("edittender.documents.knowledgebasenote")}</p>
              </HoverCardContent>
            </HoverCard>
          </div>
        );
      },
      cell: ({ row }) => (
        <div>
          {row.original.status === DocumentStatus.InKnowledgeBase ? (
            <LuBookText />
          ) : row.original.status === DocumentStatus.AddingToKnowledgeBase || row.original.status === DocumentStatus.RemovingFromKnowledgeBase ? (
            <Loader2Icon className="h-4 w-4 animate-spin text-slate-400" />
          ) : null}
        </div>
      ),
    },
    {
      accessorKey: "sheetagent",
      header: ({ column }) => {
        return (
          <div className="flex items-center gap-1">
            <TableColumnHeader hideSortingButton text="" icon={<FileSpreadsheet className="h-4 w-4" />} column={column} />
            <HoverCard>
              <HoverCardTrigger>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-5 w-5 p-0 hover:cursor-help flex items-center justify-center"
                >
                  <InformationCircleIcon className="h-5 w-5" />
                </Button>
              </HoverCardTrigger>
              <HoverCardContent>
                <h2 className="text-sm mb-2">{t("tables:documents.columns.sheetagent")}</h2>
                <p className="text-sm font-normal text-slate-400">
                  {t("edittender.documents.sheetagentnote")}
                </p>
              </HoverCardContent>
            </HoverCard>
          </div>
        );
      },
      cell: ({ row }) => {
        const sheetAgentRuns = row.original.sheetAgentRuns;
        if (!sheetAgentRuns || sheetAgentRuns.length < 1) {
          return null;
        }
        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge>{t("edittender.documents.linked")}</Badge>
            </TooltipTrigger>
            <TooltipContent>
              {sheetAgentRuns.map((run) => (
                <p key={run.id}>{run.name}</p>
              ))}
            </TooltipContent>
          </Tooltip>
        );
      },
    },
    {
      id: "actions",
      cell: ({ row }) => {
        const processing = row.original.status === DocumentStatus.AddingToKnowledgeBase || row.original.status === DocumentStatus.RemovingFromKnowledgeBase;
        const hasSheetAgentRun = !!row.original.sheetAgentRun;

        return (
          <div className="flex justify-end">
            <DropdownMenu>
              <DropdownMenuTrigger disabled={loading}><DotsHorizontalIcon className="h-4 w-4" /></DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem className="py-0" disabled={processing}>
                  <ActionButton
                    className="justify-start w-full py-2"
                    text={t("common:buttons.download")}
                    dataObj={row.original}
                    fn={downloadActionFn}
                  />
                </DropdownMenuItem>
                {userCanEdit && (
                  <>
                    {
                      row.original.format === DocumentFormat.Pdf && (
                        row.original.status === DocumentStatus.InKnowledgeBase ? (
                          <DropdownMenuItem className="py-0" disabled={processing}>
                            <ActionButton
                              className="justify-start w-full py-2"
                              text={t("edittender.documents.buttons.removefromknowledgebase")}
                              dataObj={row.original}
                              fn={removeFromKnowledgeBaseActionFn}
                            />
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem className="py-0" disabled={processing}>
                            <ActionButton
                              className="justify-start w-full py-2"
                              text={t("edittender.documents.buttons.addtoknowledgebase")}
                              dataObj={row.original}
                              fn={addToKnowledgeBaseActionFn}
                            />
                          </DropdownMenuItem>
                        )
                      )
                    }
                    {hasSheetAgentRun ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <DropdownMenuItem className="text-red-500 py-0 opacity-50 cursor-not-allowed" onSelect={(e) => e.preventDefault()}>
                            <ActionButton
                              className="justify-start w-full py-2 hover:text-red-500"
                              text={t("common:buttons.remove")}
                              dataObj={row.original}
                              fn={removeActionFn}
                              disabled={true}
                            />
                          </DropdownMenuItem>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-60">
                          <p>{t("edittender.documents.sheetagentrunmustbedeleted")}</p>
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      <DropdownMenuItem className="text-red-500 py-0" disabled={processing}>
                        <ActionButton
                          className="justify-start w-full py-2 hover:text-red-500"
                          text={t("common:buttons.remove")}
                          dataObj={row.original}
                          fn={removeActionFn}
                        />
                      </DropdownMenuItem>
                    )}
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    },
  ].filter(Boolean) as ColumnDef<Document, string>[];
};
